import {
	createI18nMessage,
	required as baseRequired,
} from '@vuelidate/validators';
import { i18n } from '@/lib/i18n';
import { type ValueOf } from '@/types';
import { timeToSeconds } from './time';
import { isValid, parseISO } from 'date-fns';

const PARSER_TYPE = {
	INT: 'int',
	FLOAT: 'float',
};

export const withI18nMessage = createI18nMessage({
	t: i18n.global.t.bind(i18n),
});

export const required = withI18nMessage(baseRequired, {
	messagePath: () => 'required_validation_error',
});

const parseNumberUserInput = (
	value: string | number,
	type: ValueOf<typeof PARSER_TYPE>
) => {
	if (typeof value === 'string') {
		if (!value.length) {
			// An empty string, the user has probably just not written anything
			// yet. If the field is required, it should have a separate "required"
			// validator
			return NaN;
		}

		switch (type) {
			case PARSER_TYPE.INT:
				return parseInt(value);
			case PARSER_TYPE.FLOAT:
				return parseFloat(value);
			default:
				return NaN;
		}
	}

	return value;
};

export const nonNegativeNumber = (value: string | number) => {
	const parsedNumber = parseNumberUserInput(value, PARSER_TYPE.FLOAT);

	if (isNaN(parsedNumber)) {
		// A valid number was not entered, it's debatable if we want to fail in
		// this case, or if there should be a separate "numberic" validator used
		return true;
	}

	return parsedNumber >= 0;
};

export const largerThanZeroNumber = (value: string | number) => {
	const parsedNumber = parseNumberUserInput(value, PARSER_TYPE.FLOAT);

	if (isNaN(parsedNumber)) {
		return true;
	}

	return parsedNumber > 0;
};

export const maximumInt = (max: number) => (value: string | number) => {
	const parsedNumber = parseNumberUserInput(value, PARSER_TYPE.INT);

	if (isNaN(parsedNumber)) {
		// A valid number was not entered, it's debatable if we want to fail in
		// this case, or if there should be a separate "numeric" validator used
		return true;
	}

	return parsedNumber < max;
};

export const validHttpUrl = (value: string) => {
	let url;
	try {
		url = new URL(value);
	} catch {
		return false;
	}

	return url.protocol === 'http:' || url.protocol === 'https:';
};

export const validateEmail = (email: string) => {
	// RFC 5322 quoted-string local-part allows control characters (\x01-\x1f etc.) —
	// they are intentional here, not typos.
	// eslint-disable-next-line no-control-regex
	return /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?$/i.test(
		email
	);
};

export const largerThanZeroTime = (timeValue: string) => {
	const time = timeToSeconds(timeValue);

	return time > 0;
};

const baseValidDate = (date: string) => {
	const parsedDate = parseISO(date);

	return isValid(parsedDate);
};

export const validDate = withI18nMessage(baseValidDate, {
	messagePath: () => 'required_validation_error',
});

const baseEmptyOrValidDate = (date: string) => {
	if (!date) {
		return true;
	}

	return baseValidDate(date);
};

export const emptyOrValidDate = withI18nMessage(baseEmptyOrValidDate, {
	messagePath: () => 'validation_error_not_valid_date',
});
